import React from "react";
import { Edit3, Trash2, Check, X } from "lucide-react";

// Helper functions for formatting
const fmt = (n) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n);
const fmtPct = (n) => `${parseFloat(n || 0).toFixed(2)}%`;

/**
 * Renders the SUTA rate cell with inline editing capability
 */
function SutaRateCell({
  item,
  editingSutaState,
  editingSutaValue,
  setEditingSutaValue,
  startEditingSuta,
  updateCurrentStateSutaRate,
  cancelEditingSuta,
}) {
  const isEditing = editingSutaState === item.state;

  if (isEditing) {
    return (
      <div className="flex items-center justify-end gap-1">
        <input
          type="number"
          step="0.01"
          value={editingSutaValue}
          onChange={e => setEditingSutaValue(e.target.value)}
          onKeyDown={e => {
            if (e.key === 'Enter') updateCurrentStateSutaRate(item.state, editingSutaValue);
            if (e.key === 'Escape') cancelEditingSuta();
          }}
          autoFocus
          className="w-16 h-6 text-right text-xs font-mono border border-red-400 rounded px-1 focus:outline-none focus:ring-1 focus:ring-red-400"
          data-testid={`edit-suta-input-${item.state}`}
        />
        <button
          onClick={() => updateCurrentStateSutaRate(item.state, editingSutaValue)}
          className="p-0.5 text-emerald-600 hover:text-emerald-700"
          title="Save"
        >
          <Check size={12} />
        </button>
        <button
          onClick={cancelEditingSuta}
          className="p-0.5 text-slate-400 hover:text-red-500"
          title="Cancel"
        >
          <X size={12} />
        </button>
      </div>
    );
  }

  return (
    <button
      onClick={() => startEditingSuta(item.state, item.sutaRate)}
      className="font-mono text-slate-700 hover:text-red-600 hover:underline cursor-pointer transition-colors"
      title={`Click to edit SUTA rate for all ${item.state} items`}
      data-testid={`edit-suta-btn-${item.state}`}
    >
      {fmtPct(item.sutaRate)}
    </button>
  );
}

/**
 * Renders a single row in the Current Costs table
 */
export function CurrentCostsRow({
  item,
  isAdp,
  adpUnbundled,
  serviceFees,
  setServiceFees,
  editingSutaState,
  editingSutaValue,
  setEditingSutaValue,
  startEditingSuta,
  updateCurrentStateSutaRate,
  cancelEditingSuta,
  startEditItem,
  removeLineItem,
  calculate,
  sutaBases,
  WC_DESCRIPTIONS,
}) {
  const c = calculate(item, sutaBases);
  const svcFeeRate = serviceFees[item.id]?.rate || 0;
  const svcFeeAmt = (item.grossWages / 100) * svcFeeRate;

  return (
    <tr 
      className="border-b border-red-100 hover:bg-red-50/40 transition-colors table-row-enter" 
      data-testid={`line-item-row-${item.id}`}
    >
      <td className="px-3 py-1.5 font-medium text-slate-900">{item.state}</td>
      <td className="px-3 py-1.5">
        <span className="font-mono text-slate-700">{item.wcCode}</span>
        <span className="text-xs text-slate-400 ml-1.5">
          {WC_DESCRIPTIONS[item.wcCode]?.substring(0, 25) || ""}
        </span>
      </td>
      <td className="px-3 py-1.5 text-right font-mono">{item.employeeCount}</td>
      <td className="px-3 py-1.5 text-right font-mono">{fmt(item.grossWages)}</td>
      <td className="px-3 py-1.5 text-right font-mono">{fmtPct(item.wcRate)}</td>
      <td className="px-3 py-1.5 text-right font-mono font-medium">{fmt(c.wcPremium)}</td>
      
      {isAdp && adpUnbundled && (
        <>
          <td className="px-3 py-1">
            <input 
              type="number" 
              step="0.01" 
              value={svcFeeRate || ""} 
              onChange={e => setServiceFees(prev => ({ 
                ...prev, 
                [item.id]: { rate: parseFloat(e.target.value) || 0 } 
              }))}
              placeholder="0.00"
              className="w-16 h-6 text-right text-xs font-mono border border-red-200 rounded px-1 focus:outline-none focus:ring-1 focus:ring-red-400"
              data-testid={`svc-fee-rate-${item.id}`}
            />
          </td>
          <td className="px-3 py-1.5 text-right font-mono font-medium text-red-600">
            {fmt(svcFeeAmt)}
          </td>
        </>
      )}
      
      <td className="px-3 py-1 text-right">
        <SutaRateCell
          item={item}
          editingSutaState={editingSutaState}
          editingSutaValue={editingSutaValue}
          setEditingSutaValue={setEditingSutaValue}
          startEditingSuta={startEditingSuta}
          updateCurrentStateSutaRate={updateCurrentStateSutaRate}
          cancelEditingSuta={cancelEditingSuta}
        />
      </td>
      <td className="px-3 py-1.5 text-right font-mono font-medium">{fmt(c.sutaTax)}</td>
      <td className="px-3 py-1.5 text-center">
        <div className="flex items-center justify-center gap-0.5">
          <button 
            data-testid={`edit-item-${item.id}`} 
            onClick={() => startEditItem(item)} 
            className="p-1 text-slate-400 hover:text-[#3c68ff] rounded-md hover:bg-blue-50 transition-all"
          >
            <Edit3 size={13} />
          </button>
          <button 
            data-testid={`delete-item-${item.id}`} 
            onClick={() => removeLineItem(item.id)} 
            className="p-1 text-slate-400 hover:text-red-500 rounded-md hover:bg-red-50 transition-all"
          >
            <Trash2 size={13} />
          </button>
        </div>
      </td>
    </tr>
  );
}
